import { getLanguage } from '@home/utils/common';

export interface IMergeSameCellsConfig {
  enabled?: boolean;
}

type TTableRow = Record<string, Record<string, unknown>>;

/** 与 AliTableCell 展示逻辑对齐的单元格文本 */
export function getMergeCellDisplayValue(
  cell: Record<string, unknown> | undefined,
  isEn: boolean,
): string {
  if (!cell || typeof cell !== 'object') {
    return '';
  }
  const val = cell.value;
  const valueEn = cell.value_en as string | undefined;
  const valueCn = cell.value_cn as string | undefined;
  const display = getLanguage(isEn, valueEn, valueCn) || val;
  if (display === null || display === undefined) {
    return '';
  }
  return String(display);
}

/**
 * 是否应对当前表格应用合并：
 * - 开关开启、非交叉表、存在维度列
 * - 排序后由 computeMergeRowSpans 按相邻相同展示值重新检测，不因指标排序禁用
 */
export function shouldApplyMergeSameCells(
  config: IMergeSameCellsConfig | undefined,
  hasCrosstab: boolean,
  dimensionCodes: string[],
): boolean {
  return !!config?.enabled && !hasCrosstab && dimensionCodes.length > 0;
}

/**
 * 各维度列按从左到右的优先级层级合并：
 * - 最左维度仅比较本列相邻展示值；
 * - 右侧维度除本列值相同外，还要求左侧所有维度列在合并区间内取值一致，
 *   不得跨越左侧维度的合并边界。
 * 返回 Map<页内 rowIndex, Record<columnCode, rowSpan>>；rowSpan=0 表示被合并隐藏。
 */
export function computeMergeRowSpans(
  rows: TTableRow[],
  dimensionColumnCodes: string[],
  isEn: boolean,
): Map<number, Record<string, number>> {
  const spanMap = new Map<number, Record<string, number>>();

  if (rows.length === 0 || dimensionColumnCodes.length === 0) {
    return spanMap;
  }

  const areLeftDimensionsEqual = (
    anchorRowIndex: number,
    targetRowIndex: number,
    leftCodes: string[],
  ): boolean =>
    leftCodes.every(leftCode => {
      const anchorLeft = getMergeCellDisplayValue(
        rows[anchorRowIndex]?.[leftCode],
        isEn,
      );
      const targetLeft = getMergeCellDisplayValue(
        rows[targetRowIndex]?.[leftCode],
        isEn,
      );
      return anchorLeft === targetLeft;
    });

  dimensionColumnCodes.forEach((code, colIdx) => {
    const leftCodes = dimensionColumnCodes.slice(0, colIdx);
    let rowIndex = 0;
    while (rowIndex < rows.length) {
      const anchorValue = getMergeCellDisplayValue(rows[rowIndex]?.[code], isEn);
      let span = 1;

      while (rowIndex + span < rows.length) {
        const nextRowIndex = rowIndex + span;
        const nextValue = getMergeCellDisplayValue(
          rows[nextRowIndex]?.[code],
          isEn,
        );
        if (nextValue !== anchorValue) {
          break;
        }
        if (!areLeftDimensionsEqual(rowIndex, nextRowIndex, leftCodes)) {
          break;
        }
        span += 1;
      }

      const anchorSpans = spanMap.get(rowIndex) ?? {};
      anchorSpans[code] = span;
      spanMap.set(rowIndex, anchorSpans);

      for (let offset = 1; offset < span; offset += 1) {
        const hiddenRowIndex = rowIndex + offset;
        const hiddenSpans = spanMap.get(hiddenRowIndex) ?? {};
        hiddenSpans[code] = 0;
        spanMap.set(hiddenRowIndex, hiddenSpans);
      }

      rowIndex += span;
    }
  });

  return spanMap;
}

function isStatisticsRow(row: Record<string, unknown>): boolean {
  return Object.values(row).some(
    cell =>
      cell &&
      typeof cell === 'object' &&
      (cell as Record<string, unknown>).is_statistics === true,
  );
}

/**
 * 通过 column.getCellProps 注入 rowSpan，交由 ali-react-table 内置 SpanManager 处理，
 * 避免在自定义 Cell 中 return null 与虚拟滚动冲突。
 */
export function applyMergeSameCellsToColumns<T extends { type?: number; code: string; getCellProps?: (...args: unknown[]) => Record<string, unknown> }>(
  columns: T[],
  mergeRowSpansMap: Map<number, Record<string, number>>,
): T[] {
  return columns.map(column => {
    if (column.type !== 1) {
      return column;
    }
    const prevGetCellProps = column.getCellProps;
    return {
      ...column,
      getCellProps(
        value: unknown,
        record: Record<string, unknown>,
        rowIndex: number,
      ) {
        const base = prevGetCellProps?.(value, record, rowIndex) ?? {};
        if (isStatisticsRow(record)) {
          return base;
        }
        const rowSpan = mergeRowSpansMap.get(rowIndex)?.[column.code];
        if (typeof rowSpan === 'number' && rowSpan > 1) {
          const prevClassName = base.className as string | undefined;
          return {
            ...base,
            rowSpan,
            className: prevClassName
              ? `${prevClassName} dbl-merge-same-cells-td`
              : 'dbl-merge-same-cells-td',
          };
        }
        return base;
      },
    };
  });
}
